import { useState, useEffect, Fragment } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import DOMPurify from "dompurify";
import moment from "moment";
import { Icon } from "@iconify/react/dist/iconify.js";
import { IconButton, Tooltip } from "@mui/material";
import "./EmailMessageList.css";
import ForwardMessageModal from "./ForwardMessageModal";
import Loader from "../Components/Loader";
import { getEmailMessageByTicket } from "../Store/Actions/TicketAction";

function EmailMessageList({ selectedEmail, ticketID }) {
  const [getDate, setGetDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [messageToForward, setMessageToForward] = useState("");
  const [openModalForwardMessage, setOpenModalForwardMessage] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  let scrollTimer = null;

  useEffect(() => {
    const renderDateFLoat = () => {
      const messageBox = document.getElementById(
        "messageListInfiniteScrollTarget"
      );

      let dateFloat = document.getElementsByClassName("DateFloat");
      if (dateFloat.length > 0) dateFloat = dateFloat[0];

      const scrollEvent = (box) => {
        dateFloat.style.top = "0vh";
        dateFloat.style.opacity = 1;
        clearTimeout(scrollTimer);

        scrollTimer = setTimeout(() => {
          dateFloat.style.top = "-2vh";
          dateFloat.style.opacity = 0;
        }, 2000);

        const dateLabels = document.querySelectorAll(".DateSeparator");
        let currentLabel = null;
        let boxR = box.getBoundingClientRect();

        dateLabels.forEach((dateLabel, idx, arr) => {
          let dlR = dateLabel.getBoundingClientRect();

          if (dlR.top > boxR.top) {
            currentLabel = idx >= dateLabels.length ? arr[idx] : arr[idx + 1];
          } else if (idx === 0) {
            currentLabel = arr[idx];
          }
        });

        if (currentLabel) {
          let el = currentLabel.getElementsByClassName("DateSeparatorVal");
          if (el && el.length > 0) setGetDate(el[0].innerText);
        }
      };

      messageBox?.removeEventListener("scroll", () => {
        scrollEvent(messageBox);
      });

      messageBox?.addEventListener("scroll", () => {
        scrollEvent(messageBox);
      });
    };

    renderDateFLoat();
  }, []);

  // Effect ran when selected email is changed
  useEffect(() => {
    fetchEmailMessageList(true);
  }, [ticketID]);

  // Effect ran when a new email comes
  // Doesn't run when used in email history
  useEffect(() => {
    fetchEmailMessageList(true, 1, true);
  }, []);

  const fetchEmailMessageList = (
    isFirstLoad,
    page,
    isUpdateChat // To check if called from effect
  ) => {
    if (isFirstLoad) {
      !isUpdateChat && setIsLoading(true);
      setPage(1);
    }

    getEmailMessageByTicket(ticketID, page)
      .then((response) => {
        if (response.status) {
          if (isFirstLoad) {
            setMessages(response.data.messages);

            setGetDate(
              response.data.messages ? response.data.messages[0].EmailTime : ""
            );
          } else {
            setMessages([...messages, ...response.data.messages]);
          }

          setTotal(response.data.total);
          setIsLoading(false);
        } else {
          console.log("error", response.error);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
      });
  };

  const separator = (index, date, otherDate) => {
    if (
      index.toString() === "dateseparator" ||
      index.toString() === "datefloat"
    ) {
      let text = date;
      if (date === moment().format("DD MMM YYYY")) text = "Today";
      else if (date === moment().add(-1, "d").format("DD MMM YYYY"))
        text = "Yesterday";
      return date === otherDate ? (
        ""
      ) : (
        <div
          className={
            index.toString() === "dateseparator" ? "DateSeparator" : "DateFloat"
          }
        >
          <span>{text}</span>
          <span className="DateSeparatorVal" style={{ display: "none" }}>
            {date}
          </span>
        </div>
      );
    }
  };

  return (
    <div className="email-message-list">
      {isLoading ? (
        <Loader />
      ) : messages?.length ? (
        <div
          className="email-message-list-content infinite-scroll-target"
          id="messageListInfiniteScrollTarget"
        >
          {separator(
            "datefloat",
            moment.utc(getDate).format("DD MMM YYYY"),
            moment.utc(getDate).add(1, "d").format("DD MMM YYYY")
          )}

          <InfiniteScroll
            dataLength={messages.length}
            hasMore={messages.length < total}
            inverse
            loader={<Loader />}
            scrollableTarget="messageListInfiniteScrollTarget"
            scrollThreshold={0.5}
            style={{
              display: "flex",
              flexDirection: "column-reverse",
            }}
            next={() => {
              fetchEmailMessageList(false, page + 1);
              setPage(page + 1);
            }}
          >
            {messages?.map((message, index, array) => {
              return (
                <Fragment key={index}>
                  <div
                    className={`message-row ${
                      message.EmailDirection === "OUTBOX" ? "agent" : "customer"
                    }`}
                  >
                    <div
                      className={`message-bubble-container ${
                        message.EmailDirection === "OUTBOX"
                          ? "agent"
                          : "customer"
                      }`}
                    >
                      <div className="message-bubble">
                        {message.EmailDirection === "OUTBOX" && (
                          <span className="sender-name">
                            {message.EmailDirection === "OUTBOX"
                              ? message.AgentName
                              : "Operator"}
                          </span>
                        )}

                        <p
                          dangerouslySetInnerHTML={{
                            // Sanitize HTML to increase security against XSS attacks
                            __html: DOMPurify.sanitize(message.BodyEmail, {
                              USE_PROFILES: { html: true },
                            }),
                          }}
                        />

                        <div className="message-info">
                          <span className="message-time">
                            {message.EmailTime &&
                              moment.utc(message.EmailTime).format("HH:mm")}
                          </span>
                        </div>
                      </div>
                    </div>

                    <Tooltip arrow placement="top" title="Forward message">
                      <IconButton
                        className="forward-message-button"
                        onClick={() => {
                          setOpenModalForwardMessage(true);
                          setMessageToForward(message);
                        }}
                      >
                        <Icon
                          height="30"
                          icon="material-symbols:forward-rounded"
                        />
                      </IconButton>
                    </Tooltip>
                  </div>

                  {separator(
                    "dateseparator",
                    moment.utc(message.EmailTime).format("DD MMM YYYY"),
                    index >= messages.length - 1
                      ? moment
                          .utc(message.EmailTime)
                          .add(-1, "d")
                          .format("DD MMM YYYY")
                      : moment
                          .utc(array[index + 1].EmailTime)
                          .format("DD MMM YYYY")
                  )}
                </Fragment>
              );
            })}
          </InfiniteScroll>
        </div>
      ) : (
        <p className="empty">No email message found.</p>
      )}

      {openModalForwardMessage && (
        <ForwardMessageModal
          caseID={ticketID}
          message={messageToForward}
          ticketNo={selectedEmail.TicketNo}
          onClose={() => {
            setOpenModalForwardMessage(false);
            setMessageToForward("");
          }}
        />
      )}
    </div>
  );
}

export default EmailMessageList;
